import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, Briefcase, FileWarning, Gauge, PauseCircle, Wallet } from "lucide-react";
import api from "../../lib/api";
import { fmtMoney, GATE_STATUS, PHASE_LABELS, PROJECT_HEALTH, PROJECT_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, KpiCard, PageHeader } from "../../components/common";

function AttentionList({ title, items, render, linkFor, testid }) {
  return (
    <div className="ts-card p-4" data-testid={testid}>
      <p className="mb-2 font-display text-sm font-semibold">{title} <span className="text-slate-400">({items.length})</span></p>
      {items.length === 0 && <p className="text-xs text-slate-400">Nada pendiente.</p>}
      <div className="space-y-1.5">
        {items.slice(0, 6).map((it) => (
          <Link key={it.id} to={linkFor(it)} className="block rounded-sm border border-slate-100 px-3 py-2 text-sm hover:border-blue-300 hover:bg-blue-50/40" data-testid={`${testid}-item-${it.id}`}>
            {render(it)}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ProyectosDashboard() {
  const { map } = useUsers();
  const { data } = useQuery({
    queryKey: ["proj-dashboard"],
    queryFn: async () => (await api.get("/proyectos/dashboard")).data,
  });
  const { data: plan } = useQuery({
    queryKey: ["plan-cuadro"],
    queryFn: async () => (await api.get("/proyectos/planning/cuadro")).data,
  });
  if (!data) return null;
  const { attention, portfolio } = data;

  return (
    <div data-testid="proyectos-dashboard">
      <PageHeader title="Proyectos — Cuadro de mando" subtitle="Estado real del portafolio: qué requiere atención, en qué fase está cada proyecto y quién debe actuar." />

      <h2 className="ts-section-title mb-3">Requiere atención</h2>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AttentionList title="Proyectos bloqueados" items={attention.bloqueados} testid="att-bloqueados"
          linkFor={(p) => `/proyectos/${p.id}`}
          render={(p) => <><span className="font-medium">{p.name}</span><p className="text-xs text-red-600">{p.motivo || "Bloqueado"}</p></>} />
        <AttentionList title="Proyectos en riesgo" items={attention.en_riesgo} testid="att-riesgo"
          linkFor={(p) => `/proyectos/${p.id}`}
          render={(p) => <span className="font-medium">{p.name}</span>} />
        <AttentionList title="Puertas de fase en revisión" items={attention.gates_en_revision} testid="att-gates"
          linkFor={(g) => `/proyectos/${g.project_id}?tab=fases`}
          render={(g) => <span className="font-medium">{g.label}</span>} />
        <AttentionList title="Pendientes de aprobación de apertura" items={attention.pendientes_aprobacion} testid="att-apertura"
          linkFor={(p) => `/proyectos/${p.id}`}
          render={(p) => <span className="font-medium">{p.name}</span>} />
        <AttentionList title="Datos desactualizados o incompletos" items={attention.datos_desactualizados} testid="att-dq"
          linkFor={(p) => `/proyectos/${p.id}`}
          render={(p) => <><span className="font-medium">{p.name}</span><span className="ml-2"><Chip map={{ desactualizado: { label: "Desactualizado", tone: "warning" }, incompleto: { label: "Incompleto", tone: "danger" } }} value={p.quality} /></span></>} />
        <div className="ts-card flex flex-col justify-between p-4" data-testid="att-gates-lista">
          <p className="font-display text-sm font-semibold">Puertas listas para revisión</p>
          <p className="my-2 font-mono text-3xl font-bold text-slate-900">{attention.gates_lista_revision}</p>
          <Link to="/proyectos/lista" className="text-xs font-medium text-blue-700 hover:underline">Ir a proyectos →</Link>
        </div>
      </div>

      {plan && (
        <>
          <h2 className="ts-section-title mb-3 mt-8">Planificación y control de avance</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <AttentionList title="Proyectos con retraso sobre baseline" items={plan.delayed} testid="plan-delayed"
              linkFor={(p) => `/proyectos/planificacion?project=${p.id}&tab=desviaciones`}
              render={(p) => <><span className="font-medium">{p.name}</span><span className="float-right font-mono text-xs font-bold text-red-600">+{p.delay_days} días</span></>} />
            <AttentionList title="Hitos críticos en riesgo o retrasados" items={plan.critical_milestones} testid="plan-milestones"
              linkFor={(m) => `/proyectos/planificacion?project=${m.project_id}&tab=desviaciones`}
              render={(m) => <><span className="font-medium">{m.name}</span><p className="text-xs text-slate-500">{m.status.replace("_", " ")}</p></>} />
            <div className="grid gap-4">
              <Link to="/proyectos/planificacion" className="ts-card block p-4 hover:border-blue-300" data-testid="plan-risks-kpi">
                <p className="ts-caption">Riesgos de retraso abiertos</p>
                <p className="mt-1 font-mono text-2xl font-bold text-slate-900">{plan.open_schedule_risks}</p>
                <p className="text-xs text-slate-400">Ir a planificación →</p>
              </Link>
              <Link to="/proyectos/recursos" className="ts-card block p-4 hover:border-blue-300" data-testid="plan-resources-kpi">
                <p className="ts-caption">Actividades críticas vencidas / Recovery abiertos</p>
                <p className="mt-1 font-mono text-2xl font-bold text-slate-900">{plan.overdue_critical_activities} <span className="text-sm font-normal text-slate-400">/ {plan.open_recovery_plans}</span></p>
                <p className="text-xs text-slate-400">Ir a recursos →</p>
              </Link>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">Retraso medio del portafolio: {plan.avg_delay_days} días · {plan.on_time.length} proyectos en plazo.</p>
        </>
      )}

      <h2 className="ts-section-title mb-3 mt-8">Portafolio</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-portfolio-total" title="Proyectos" value={`${portfolio.activos} activos / ${portfolio.total}`} icon={Briefcase} tone="info" to="/proyectos/portafolio" />
        <KpiCard testid="kpi-portfolio-budget" title="Presupuesto actual" value={fmtMoney(portfolio.totals.presupuesto_actual)} subtitle={`Forecast ${fmtMoney(portfolio.totals.forecast_final)}`} icon={Wallet} tone="muted" to="/proyectos/portafolio" />
        <KpiCard testid="kpi-portfolio-committed" title="CAPEX comprometido" value={fmtMoney(portfolio.totals.comprometido)} subtitle={`Pagado ${fmtMoney(portfolio.totals.pagado)}`} icon={Gauge} tone="info" to="/proyectos/portafolio" />
        <KpiCard testid="kpi-portfolio-risk" title="En riesgo / bloqueados" value={`${attention.en_riesgo.length} / ${attention.bloqueados.length}`} icon={AlertOctagon} tone={attention.bloqueados.length ? "danger" : "warning"} to="/proyectos/lista?status=bloqueado" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="ts-card p-5" data-testid="portfolio-by-phase">          <p className="mb-3 font-display text-sm font-semibold">Por fase</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(portfolio.by_phase).map(([ph, n]) => (
              <Link key={ph} to={`/proyectos/lista?phase=${ph}`} className="ts-chip ts-chip-info hover:ring-1 hover:ring-blue-400" data-testid={`phase-${ph}`}>
                {PHASE_LABELS[ph] || ph} · {n}
              </Link>
            ))}
          </div>
        </div>
        <div className="ts-card p-5" data-testid="portfolio-by-health">
          <p className="mb-3 font-display text-sm font-semibold">Por salud</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(portfolio.by_health).map(([hk, n]) => (
              <Link key={hk} to={`/proyectos/lista?health=${hk}`} className="hover:opacity-80" data-testid={`health-${hk}`}>
                <Chip map={PROJECT_HEALTH} value={hk} /> <span className="ml-0.5 font-mono text-xs">{n}</span>
              </Link>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">Cada indicador permite drill-down al listado filtrado y de ahí al proyecto.</p>
        </div>
      </div>
    </div>
  );
}
